import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useFocusPulse from '../../hooks/useFocusPulse';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';
import {
  Sparkles, Loader2, Plus, Trash2, Save, Download, FileText,
  ChevronDown, ChevronUp, Bot, X, StickyNote, Layers, Target,
  BookOpen, Edit3, Settings2, Award, AlertCircle, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { describeApiError } from '../../lib/apiErrors';
import CollapsibleList from './CollapsibleList';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Summary Bar ──────────────────────────────────────────────────
function SummaryBar({ summary }) {
  const s = summary || {};
  const total = s.total_requirements || 0;
  const guidanceDone = s.guidance_complete || 0;
  const guidancePct = total > 0 ? Math.round((guidanceDone / total) * 100) : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5" data-testid="rtm-summary">
      <div className="bg-white rounded-lg border border-slate-200 p-2.5 text-center">
        <p className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{total}</p>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Requirements</p>
      </div>
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-2.5 text-center">
        <p className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.volumes || 0}</p>
        <p className="text-[10px] text-blue-600 uppercase tracking-wider">Volumes</p>
      </div>
      <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-2.5 text-center">
        <p className="text-xl font-bold text-indigo-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.factors || 0}</p>
        <p className="text-[10px] text-indigo-600 uppercase tracking-wider">Eval Factors</p>
      </div>
      <div className={cn('rounded-lg border p-2.5 text-center',
        guidancePct >= 80 ? 'bg-emerald-50 border-emerald-200'
          : guidancePct >= 40 ? 'bg-amber-50 border-amber-200'
          : 'bg-slate-50 border-slate-200'
      )}>
        <p className={cn('text-xl font-bold',
          guidancePct >= 80 ? 'text-emerald-700' : guidancePct >= 40 ? 'text-amber-700' : 'text-slate-500'
        )} style={{ fontFamily: 'Manrope, sans-serif' }}>
          {guidanceDone}/{total}
        </p>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Guidance Done</p>
      </div>
    </div>
  );
}

// ── Single requirement row ────────────────────────────────────────
function RTMRow({ entry, onUpdate, onDelete, onOpenGuidance }) {
  const hasGuidance = !!entry.writer_guidance;
  const winTheme    = entry.win_theme || '';

  return (
    <div
      data-testid={`rtm-entry-${entry.id}`}
      className="grid gap-2 items-start px-3 py-2.5 border-b border-slate-100 transition-colors text-xs hover:bg-slate-50/50"
      style={{ gridTemplateColumns: '1fr 3fr 0.7fr 0.5fr 2fr auto' }}
    >
      {/* Requirement Group */}
      <div>
        <p className="font-medium text-slate-600 leading-tight">
          {entry.requirement_group || <span className="text-slate-300 italic">No group</span>}
        </p>
      </div>

      {/* Requirement Text */}
      <div>
        <p className="text-slate-800 leading-snug line-clamp-3">{entry.requirement_text}</p>
      </div>

      {/* RFQ/SOW Reference */}
      <div className="text-center">
        <span className="inline-block font-mono text-[10px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 leading-tight">
          {entry.section_reference || '—'}
        </span>
      </div>

      {/* Win Theme # */}
      <div className="text-center">
        {winTheme ? (
          <span className="inline-block text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 leading-tight">
            {winTheme}
          </span>
        ) : (
          <span className="text-slate-300 text-[10px]">—</span>
        )}
      </div>

      {/* Writer Guidance */}
      <div>
        {hasGuidance ? (
          <p className="text-slate-700 leading-snug line-clamp-3 text-[11px]">{entry.writer_guidance}</p>
        ) : (
          <p className="text-slate-300 italic text-[11px]">No guidance yet</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 justify-end">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onOpenGuidance(entry)}
                className={cn(
                  'p-1 rounded transition-colors',
                  hasGuidance
                    ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                    : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50',
                )}
                title={hasGuidance ? 'Edit writer guidance' : 'Add writer guidance'}
                data-testid={`entry-guidance-${entry.id}`}
              >
                <StickyNote className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p className="text-xs">{hasGuidance ? 'Edit writer guidance' : 'Add writer guidance'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          variant="ghost" size="sm"
          className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
          onClick={() => onDelete(entry.id)}
          data-testid={`entry-delete-${entry.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ── Hierarchy Group Headers ───────────────────────────────────────
function VolumeHeader({ label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 text-white mt-3 first:mt-0">
      <Layers className="w-3.5 h-3.5 text-amber-400 shrink-0" />
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
    </div>
  );
}

function FactorHeader({ label, subFactor, count }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-100 border-b border-slate-200">
      <div className="flex items-center gap-2">
        <Target className="w-3 h-3 text-indigo-500 shrink-0" />
        <span className="text-[11px] font-semibold text-slate-700">{label}</span>
        {subFactor && (
          <>
            <span className="text-slate-300 text-[10px]">›</span>
            <span className="text-[10px] text-slate-500 italic">{subFactor}</span>
          </>
        )}
      </div>
      <Badge className="text-[9px] bg-indigo-100 text-indigo-600 border-indigo-200">{count} req{count !== 1 ? 's' : ''}</Badge>
    </div>
  );
}

// ── Column Headers ────────────────────────────────────────────────
function ColumnHeaders() {
  return (
    <div
      className="grid gap-2 items-center px-3 py-2 bg-slate-50 border-y border-slate-200 text-[9px] font-semibold uppercase tracking-wider text-slate-500 sticky top-0 z-10"
      style={{ gridTemplateColumns: '1fr 3fr 0.7fr 0.5fr 2fr auto' }}
    >
      <div>Req. Group</div>
      <div>Requirement</div>
      <div className="text-center">RFQ Ref</div>
      <div className="text-center">Win #</div>
      <div>Writer Guidance</div>
      <div className="w-14 text-right">Actions</div>
    </div>
  );
}

// ── Gap Status config ─────────────────────────────────────────────
const GAP_STATUS_OPTIONS = [
  { value: '',                label: '— Not started',    bg: 'bg-white',           text: 'text-slate-500',   border: 'border-slate-200' },
  { value: 'COMPLETE',        label: '✅ Complete',       bg: 'bg-emerald-50',      text: 'text-emerald-700', border: 'border-emerald-200' },
  { value: 'ADEQUATE',        label: '🟡 Adequate',       bg: 'bg-yellow-50',       text: 'text-yellow-700',  border: 'border-yellow-200' },
  { value: 'ACTION_REQUIRED', label: '🟠 Action Required',bg: 'bg-orange-50',       text: 'text-orange-700',  border: 'border-orange-200' },
  { value: 'CRITICAL',        label: '🔴 Critical',       bg: 'bg-red-50',          text: 'text-red-700',     border: 'border-red-200' },
];
const GAP_ROW_BG = {
  COMPLETE:        'bg-emerald-50/60',
  ADEQUATE:        'bg-yellow-50/60',
  ACTION_REQUIRED: 'bg-orange-50/60',
  CRITICAL:        'bg-red-50/70',
};

// ── Gap Check Panel ───────────────────────────────────────────────
function GapCheckPanel({ entries, pursuitId, token, onEntriesUpdate }) {
  const [open, setOpen]         = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  // local edits: entry_id → {gap_draft_location, gap_status}
  const [localEdits, setLocalEdits] = useState({});

  // Merge local edits with server state
  const displayEntries = entries.map(e => ({
    ...e,
    ...(localEdits[e.id] || {}),
  }));

  const patchEntry = async (entryId, patch) => {
    try {
      await fetch(`${API}/api/pursuits/${pursuitId}/rtm/entries/${entryId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      // propagate to parent so export uses fresh data
      onEntriesUpdate(prev => prev.map(e =>
        e.id === entryId ? { ...e, ...patch } : e
      ));
    } catch (err) {
      toast.error('Failed to save gap update');
    }
  };

  const handleLocationChange = (entryId, val) => {
    setLocalEdits(p => ({ ...p, [entryId]: { ...(p[entryId] || {}), gap_draft_location: val } }));
  };

  const handleLocationBlur = (entryId) => {
    const loc = (localEdits[entryId] || {}).gap_draft_location ?? '';
    patchEntry(entryId, { gap_draft_location: loc });
  };

  const handleStatusChange = (entryId, val) => {
    setLocalEdits(p => ({ ...p, [entryId]: { ...(p[entryId] || {}), gap_status: val } }));
    patchEntry(entryId, { gap_status: val });
  };

  const handleAiSuggest = async () => {
    setAiLoading(true);
    try {
      const res  = await fetch(`${API}/api/pursuits/${pursuitId}/rtm/gap-check/ai-suggest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'AI suggest failed');
        return;
      }
      if (data.applied === 0) {
        toast.info(data.message || 'Nothing new to suggest');
      } else {
        // Apply to local edits so UI refreshes immediately
        const newEdits = { ...localEdits };
        for (const sug of (data.suggestions || [])) {
          if (sug.entry_id && sug.gap_draft_location) {
            const existing = entries.find(e => e.id === sug.entry_id);
            if (existing && !(existing.gap_draft_location || '').trim()) {
              newEdits[sug.entry_id] = {
                gap_draft_location: sug.gap_draft_location,
                gap_status: sug.gap_status || 'ACTION_REQUIRED',
              };
            }
          }
        }
        setLocalEdits(newEdits);
        toast.success(`AI mapped ${data.applied} requirement(s)`, { description: data.message });
      }
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  // Summary stats
  const total    = displayEntries.length;
  const complete = displayEntries.filter(e => e.gap_status === 'COMPLETE').length;
  const adequate = displayEntries.filter(e => e.gap_status === 'ADEQUATE').length;
  const action   = displayEntries.filter(e => e.gap_status === 'ACTION_REQUIRED').length;
  const critical = displayEntries.filter(e => e.gap_status === 'CRITICAL').length;
  const mapped   = complete + adequate + action + critical;
  const pct      = total > 0 ? Math.round((complete / total) * 100) : 0;
  const allGreen = total > 0 && complete === total;

  // Group by volume for display
  const byVolume = displayEntries.reduce((acc, e) => {
    const vol = e.submittal_volume || '— Unassigned';
    if (!acc[vol]) acc[vol] = [];
    acc[vol].push(e);
    return acc;
  }, {});

  return (
    <div className="border-t border-slate-200 mt-0">
      {/* Toggle */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'w-full flex items-center justify-between px-5 py-3 transition-colors text-left',
          allGreen ? 'bg-emerald-900/90 hover:bg-emerald-800' : 'bg-slate-900 hover:bg-slate-800',
        )}
      >
        <div className="flex items-center gap-2.5">
          <span className="text-base">📋</span>
          <span className="text-sm font-semibold text-white">Gap Check</span>
          <span className="text-[10px] text-slate-400">requirement → draft location → status</span>
        </div>
        <div className="flex items-center gap-2">
          {total > 0 && (
            <>
              <Badge className={cn('text-[10px]', allGreen
                ? 'bg-emerald-500/30 text-emerald-300 border-emerald-500/30'
                : 'bg-white/10 text-slate-300 border-white/20')}>
                {complete}/{total} complete
              </Badge>
              {critical > 0 && (
                <Badge className="bg-red-500/30 text-red-300 border-red-500/30 text-[10px]">
                  {critical} critical
                </Badge>
              )}
            </>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="bg-white">
          {/* Stats + AI bar */}
          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-3 flex-wrap">
              {[
                { label: 'Complete',        val: complete, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                { label: 'Adequate',        val: adequate, cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
                { label: 'Action Required', val: action,   cls: 'bg-orange-100 text-orange-700 border-orange-200' },
                { label: 'Critical',        val: critical, cls: 'bg-red-100 text-red-700 border-red-200' },
                { label: 'Not started',     val: total - mapped, cls: 'bg-slate-100 text-slate-500 border-slate-200' },
              ].filter(s => s.val > 0).map(s => (
                <span key={s.label} className={cn('text-[10px] font-semibold px-2 py-0.5 rounded border', s.cls)}>
                  {s.val} {s.label}
                </span>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAiSuggest}
              disabled={aiLoading || entries.length === 0}
              className="text-xs gap-1.5 h-7 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiLoading ? 'Mapping…' : 'AI Suggest Locations'}
            </Button>
          </div>

          {/* Progress bar */}
          {total > 0 && (
            <div className="px-5 pt-3 pb-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Submission readiness</span>
                <span className={cn('text-[10px] font-bold', allGreen ? 'text-emerald-600' : 'text-slate-600')}>{pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all duration-500', allGreen ? 'bg-emerald-500' : 'bg-blue-500')}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}

          {/* Gap table */}
          {entries.length === 0 ? (
            <div className="px-5 py-8 text-center text-slate-400 text-sm">
              No compliance matrix entries yet. Build the matrix first.
            </div>
          ) : (
            <div className="px-5 py-3 space-y-4">
              {/* Column headers */}
              <div className="grid gap-2 text-[9px] font-semibold uppercase tracking-wider text-slate-400 px-2"
                style={{ gridTemplateColumns: '3fr 3fr 1.5fr' }}>
                <div>Requirement</div>
                <div>Where Addressed in Current Drafts</div>
                <div>Action / Status</div>
              </div>

              {Object.entries(byVolume).map(([vol, volEntries]) => (
                <div key={vol}>
                  {/* Volume header */}
                  <div className="flex items-center gap-2 px-2 py-1.5 bg-[#2E5496] rounded-sm mb-1">
                    <span className="text-[10px] font-bold text-white uppercase tracking-wide">{vol}</span>
                    <span className="text-[9px] text-blue-200 ml-auto">
                      {volEntries.filter(e => e.gap_status === 'COMPLETE').length}/{volEntries.length} complete
                    </span>
                  </div>

                  {/* Rows */}
                  {volEntries.map(entry => {
                    const st  = entry.gap_status || '';
                    const cfg = GAP_STATUS_OPTIONS.find(o => o.value === st) || GAP_STATUS_OPTIONS[0];
                    const rowBg = GAP_ROW_BG[st] || '';
                    return (
                      <div
                        key={entry.id}
                        className={cn(
                          'grid gap-2 items-start px-2 py-2 border-b border-slate-100 text-xs rounded-sm',
                          rowBg,
                        )}
                        style={{ gridTemplateColumns: '3fr 3fr 1.5fr' }}
                      >
                        {/* Requirement */}
                        <div>
                          <p className="font-medium text-slate-700 leading-tight line-clamp-2">
                            {entry.eval_factor || entry.requirement_group || <span className="text-slate-300 italic">No label</span>}
                          </p>
                          {entry.section_reference && (
                            <span className="font-mono text-[9px] text-slate-400">{entry.section_reference}</span>
                          )}
                        </div>

                        {/* Where addressed */}
                        <div>
                          <Input
                            value={(localEdits[entry.id] || {}).gap_draft_location ?? (entry.gap_draft_location || '')}
                            onChange={e => handleLocationChange(entry.id, e.target.value)}
                            onBlur={() => handleLocationBlur(entry.id)}
                            placeholder="Vol I Sec 2.1, or Not yet drafted"
                            className="h-7 text-xs border-slate-200 bg-white/80"
                          />
                        </div>

                        {/* Status select */}
                        <div>
                          <Select
                            value={(localEdits[entry.id] || {}).gap_status ?? (entry.gap_status || '')}
                            onValueChange={val => handleStatusChange(entry.id, val)}
                          >
                            <SelectTrigger className={cn('h-7 text-xs border', cfg.bg, cfg.text, cfg.border)}>
                              <SelectValue placeholder="— Not started" />
                            </SelectTrigger>
                            <SelectContent>
                              {GAP_STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {allGreen && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded text-emerald-700 text-xs font-semibold">
                  <span>✅</span> All requirements addressed — Submission Gate PASSED
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Matrix Context Panel ──────────────────────────────────────────
// Wrapper sections that appear above the detailed matrix in the export:
// Win Themes · Scope Note · Eval Factors · Award Approach · Submission
function MatrixContextPanel({ meta, onChange, onSave, saving }) {
  const [open, setOpen] = useState(false);

  const update = (key, val) => {
    const next = { ...meta, [key]: val };
    onChange(next);
    onSave({ [key]: val });
  };

  // ── Win Themes helpers ────────────────────────────────────────────
  const addTheme = () => {
    const themes = [...(meta.win_themes || []), { number: (meta.win_themes?.length || 0) + 1, theme_name: '', evidence: '' }];
    update('win_themes', themes);
  };
  const updateTheme = (i, field, val) => {
    const themes = meta.win_themes.map((t, idx) => idx === i ? { ...t, [field]: val } : t);
    update('win_themes', themes);
  };
  const removeTheme = (i) => {
    update('win_themes', meta.win_themes.filter((_, idx) => idx !== i).map((t, idx) => ({ ...t, number: idx + 1 })));
  };

  // ── Scope exclusions helpers ──────────────────────────────────────
  const addExclusion = () => update('scope_note_exclusions', [...(meta.scope_note_exclusions || []), '']);
  const updateExclusion = (i, val) => {
    update('scope_note_exclusions', meta.scope_note_exclusions.map((e, idx) => idx === i ? val : e));
  };
  const removeExclusion = (i) => {
    update('scope_note_exclusions', meta.scope_note_exclusions.filter((_, idx) => idx !== i));
  };

  const completedThemes = (meta.win_themes || []).filter(t => t.theme_name).length;

  return (
    <div className="border-b border-slate-200">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-2.5 bg-slate-800/95 hover:bg-slate-700 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <Settings2 className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-white">Matrix Setup</span>
          <span className="text-[10px] text-slate-400">scope note · win themes · submission info</span>
          {saving && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
        </div>
        <div className="flex items-center gap-2">
          {completedThemes > 0 && (
            <Badge className="bg-indigo-500/30 text-indigo-300 border-indigo-500/30 text-[10px]">
              <Award className="w-2.5 h-2.5 mr-1" />{completedThemes} theme{completedThemes !== 1 ? 's' : ''}
            </Badge>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="bg-slate-50 px-5 py-5 space-y-6 border-b border-slate-200">

          {/* ── Win Themes ─────────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-indigo-600" />
                <h4 className="text-sm font-semibold text-slate-800">Win Themes</h4>
                <span className="text-[10px] text-slate-400">define 4–6 discriminators before writing matrix rows</span>
              </div>
              <Button size="sm" variant="outline" onClick={addTheme} className="h-7 text-xs gap-1">
                <Plus className="w-3 h-3" />Add Theme
              </Button>
            </div>
            {(meta.win_themes || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No win themes defined. Add 4–6 discriminators that answer "why should the Government pick us?"</p>
            ) : (
              <div className="space-y-2">
                {(meta.win_themes || []).map((wt, i) => (
                  <div key={i} className="grid gap-2 items-start p-2 bg-white rounded border border-slate-200"
                    style={{ gridTemplateColumns: '0.3fr 1.5fr 3fr auto' }}>
                    <div className="flex items-center justify-center h-7 w-7 rounded bg-indigo-100 text-indigo-700 text-xs font-bold">{wt.number}</div>
                    <Input
                      value={wt.theme_name}
                      onChange={e => updateTheme(i, 'theme_name', e.target.value)}
                      onBlur={() => onSave({ win_themes: meta.win_themes })}
                      placeholder="Theme name (4–7 words)"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={wt.evidence}
                      onChange={e => updateTheme(i, 'evidence', e.target.value)}
                      onBlur={() => onSave({ win_themes: meta.win_themes })}
                      placeholder="Evidence / discriminator (contracts, dates, numbers)"
                      className="h-7 text-xs"
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeTheme(i)}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Scope Note ─────────────────────────────────────────── */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <h4 className="text-sm font-semibold text-slate-800">Matrix Scope Note</h4>
              <span className="text-[10px] text-slate-400">what's included, what's explicitly excluded</span>
            </div>
            <Textarea
              value={meta.scope_note || ''}
              onChange={e => setMeta(p => ({ ...p, scope_note: e.target.value }))}
              onBlur={e => onSave({ scope_note: e.target.value })}
              placeholder="This matrix focuses on... Excluded sections are listed below because they are handled procedurally..."
              className="min-h-[70px] text-xs bg-white"
            />
            <div className="mt-2 space-y-1">
              {(meta.scope_note_exclusions || []).map((excl, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-slate-400 text-xs">Excluded:</span>
                  <Input
                    value={excl}
                    onChange={e => updateExclusion(i, e.target.value)}
                    onBlur={() => onSave({ scope_note_exclusions: meta.scope_note_exclusions })}
                    placeholder="e.g. SOW Section 9 — Security Requirements (handled procedurally)"
                    className="h-7 text-xs flex-1 bg-white"
                  />
                  <Button size="sm" variant="ghost" onClick={() => removeExclusion(i)}
                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addExclusion} className="h-7 text-xs gap-1 mt-1">
                <Plus className="w-3 h-3" />Add Exclusion
              </Button>
            </div>
          </div>

          {/* Evaluation Factors moved to the Section M card (Submittal Volumes
              & Evaluation Factors) shown above the matrix in Manage Documents. */}

          {/* ── Award Approach + Submission Method ─────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                <Label className="text-xs font-semibold text-slate-700">Award Approach</Label>
              </div>
              <Textarea
                value={meta.award_approach || ''}
                onChange={e => setMeta(p => ({ ...p, award_approach: e.target.value }))}
                onBlur={e => onSave({ award_approach: e.target.value })}
                placeholder="Best-value tradeoff. The Government may determine that a higher-rated technical quotation is worth a higher price..."
                className="min-h-[60px] text-xs bg-white"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-3.5 h-3.5 text-slate-600" />
                <Label className="text-xs font-semibold text-slate-700">Submission Method</Label>
                <span className="text-[10px] text-slate-400">(for export header)</span>
              </div>
              <Input
                value={meta.submission_method || ''}
                onChange={e => setMeta(p => ({ ...p, submission_method: e.target.value }))}
                onBlur={e => onSave({ submission_method: e.target.value })}
                placeholder="Email to co@agency.gov AND pm@agency.gov by 5:00 PM EDT"
                className="text-xs bg-white"
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
}


// ── Main Component ────────────────────────────────────────────────
export default function ComplianceMatrix({ pursuitId, token }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDetected, setAiDetected] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // Writer Guidance dialog — AI-populated field, also user-editable.
  // The AI batch endpoint writes this field; manual edits are preserved
  // by the frontend: on re-run, only rows with empty writer_guidance are patched.
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [guidanceEntry, setGuidanceEntry] = useState(null);
  const [guidanceDraft, setGuidanceDraft] = useState('');

  // Section M gate — block/prompt before building if eval factors not extracted
  const [gateOpen, setGateOpen] = useState(false);
  const [gateChecking, setGateChecking] = useState(false);
  const [extractingSectionM, setExtractingSectionM] = useState(false);

  const emptyEntry = {
    requirement_id: '',
    section_reference: '',
    requirement_text: '',
    submittal_volume: '',
    eval_factor: '',
    eval_sub_factor: '',
    requirement_group: '',
    win_theme: '',
    writer_guidance: '',
    task_area: '',
  };
  const [newEntry, setNewEntry] = useState({ ...emptyEntry });

  // ── Matrix metadata (wrapper sections) ───────────────────────────
  const EMPTY_META = {
    win_themes: [],               // [{number, theme_name, evidence}]
    scope_note: '',
    scope_note_exclusions: [],
    eval_factors: [],             // [{factor_name, criteria}]
    award_approach: '',
    submission_method: '',
    critical_dates: [],           // [{date, activity, notes, is_critical}]
  };
  const [meta, setMeta] = useState(EMPTY_META);
  const [metaSaving, setMetaSaving] = useState(false);

  useEffect(() => {
    if (!pursuitId || !token) return;
    fetch(`${API}/api/pursuits/${pursuitId}/rtm/meta`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => setMeta(prev => ({ ...EMPTY_META, ...data })))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pursuitId, token]);

  const saveMeta = useCallback(async (patch) => {
    setMetaSaving(true);
    try {
      await fetch(`${API}/api/pursuits/${pursuitId}/rtm/meta`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
    } catch (e) {
      toast.error('Failed to save matrix context');
    } finally {
      setMetaSaving(false);
    }
  }, [pursuitId, token]);

  // Focus-pulse for Coverage Map deep-links
  useFocusPulse({
    rows: entries,
    getSelector: (id) => `[data-testid="rtm-entry-${id}"]`,
  });

  // Eval-factor suggestions from existing entries
  const volumeSuggestions = useMemo(() => {
    const set = new Set();
    entries.forEach(e => { if (e.submittal_volume?.trim()) set.add(e.submittal_volume.trim()); });
    return Array.from(set).sort();
  }, [entries]);

  const factorSuggestions = useMemo(() => {
    const set = new Set();
    entries.forEach(e => { if (e.eval_factor?.trim()) set.add(e.eval_factor.trim()); });
    return Array.from(set).sort();
  }, [entries]);

  // Load RTM
  useEffect(() => {
    if (!pursuitId || !token) return;
    fetch(`${API}/api/pursuits/${pursuitId}/rtm`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setEntries(data.entries || []);
        setSummary(data.summary || {});
        if (data.updated_at) setLastSaved(data.updated_at);
      })
      .catch(() => {});
  }, [pursuitId, token]);

  // Recompute summary locally
  const recompute = useCallback((list) => {
    const total = list.length;
    const volumes = new Set(list.map(e => e.submittal_volume).filter(Boolean)).size;
    const factors = new Set(list.map(e => e.eval_factor).filter(Boolean)).size;
    const guidanceDone = list.filter(e => e.writer_guidance).length;
    setSummary({
      total_requirements: total,
      volumes,
      factors,
      guidance_complete: guidanceDone,
    });
  }, []);

  // Save — always use preserve_edits=false so re-running the AI build
  // replaces old entries entirely instead of accumulating stale rows.
  // Manual edits are preserved at the frontend level (apply() guards
  // writer_guidance), so the DB never needs to re-inject old rows.
  const handleSave = async (silent = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/rtm?preserve_edits=false`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        setLastSaved(new Date().toISOString());
        if (!silent) toast.success('Compliance Matrix saved');
      } else if (!silent) toast.error('Save failed');
    } catch { if (!silent) toast.error('Network error'); }
    setSaving(false);
  };

  // Auto-save after AI batches settle
  const autosaveTimerRef = useRef(null);
  useEffect(() => {
    if (entries.length === 0) return;
    const anyPending = aiDetected?.batch_status?.includes('pending');
    if (anyPending) return;
    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { handleSave(true); }, 800);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, aiDetected?.batch_status]);

  // Download PDF
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/rtm/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'Compliance_Matrix.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else toast.error('PDF generation failed');
    } catch { toast.error('Network error'); }
    setDownloading(false);
  };

  // Download Word
  const handleDownloadDocx = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/rtm/docx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'Compliance_Matrix.docx';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('Word doc downloaded');
      } else toast.error('Word generation failed');
    } catch { toast.error('Network error'); }
    setDownloading(false);
  };

  // Update entry locally
  const handleUpdateEntry = useCallback((entryId, updates) => {
    setEntries(prev => {
      const updated = prev.map(e => e.id === entryId ? { ...e, ...updates } : e);
      recompute(updated);
      return updated;
    });
  }, [recompute]);

  // Delete entry locally
  const handleDeleteEntry = useCallback((entryId) => {
    setEntries(prev => {
      const updated = prev.filter(e => e.id !== entryId);
      recompute(updated);
      return updated;
    });
  }, [recompute]);

  // Writer Guidance dialog handlers
  const openGuidance = useCallback((entry) => {
    setGuidanceEntry(entry);
    setGuidanceDraft(entry.writer_guidance || '');
    setGuidanceOpen(true);
  }, []);

  const saveGuidance = useCallback(() => {
    if (!guidanceEntry) return;
    handleUpdateEntry(guidanceEntry.id, { writer_guidance: guidanceDraft });
    setGuidanceOpen(false);
    setGuidanceEntry(null);
    setGuidanceDraft('');
    toast.success('Writer guidance saved');
  }, [guidanceEntry, guidanceDraft, handleUpdateEntry]);

  // Add entry from modal
  const handleAddEntry = () => {
    if (!newEntry.requirement_text.trim()) { toast.error('Requirement text required'); return; }
    const entry = { id: crypto.randomUUID(), ...newEntry };
    setEntries(prev => { const n = [...prev, entry]; recompute(n); return n; });
    setAddModalOpen(false);
    setNewEntry({ ...emptyEntry });
    toast.success('Requirement added');
  };

  // ── Section M gate ───────────────────────────────────────────────
  // Before building, confirm Section M evaluation factors were extracted.
  // The matrix maps requirements → eval factors → volumes, so building
  // without Section M produces a weaker, factor-less matrix.
  const checkSectionMThenBuild = async () => {
    setGateChecking(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/strategy/section-m`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      const count = data?.count ?? (data?.factors?.length || 0);
      if (count > 0) {
        handleAiMap();          // factors present → build straight away
      } else {
        setGateOpen(true);      // none → prompt the user
      }
    } catch {
      // On check failure, don't block — let them build.
      handleAiMap();
    } finally {
      setGateChecking(false);
    }
  };

  // Run the Section M extractor (Strategy stage) then continue to build.
  const extractSectionMThenBuild = async () => {
    setExtractingSectionM(true);
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/strategy/section-m/extract?preserve_edits=true`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(describeApiError(data, 'Section M extraction failed'));
        return;
      }
      toast.success(
        `Extracted ${data.volumes_count || 0} volume(s) + ${data.extracted_count || 0} eval factor(s)`,
      );
      setExtractingSectionM(false);
      handleAiMap();            // proceed to build with factors now present
    } catch (e) {
      toast.error('Section M extraction failed');
      setExtractingSectionM(false);
    }
  };

  // Expand a batch's `cells` (one per requirement × submittal × eval-factor)
  // into matrix entries. A requirement that spans multiple volumes/factors
  // becomes multiple cell-entries sharing its requirement_id. Replaces any
  // prior entries for the batched requirements (so re-runs are idempotent).
  const expandWithCells = (prev, batchReqIds, cells) => {
    const reqSet = new Set(batchReqIds);
    const cellsByReq = {};
    (cells || []).forEach(c => {
      if (c && c.requirement_id) (cellsByReq[c.requirement_id] = cellsByReq[c.requirement_id] || []).push(c);
    });
    // First existing entry per requirement = the "seed" carrying base fields
    // (requirement_text, section_reference, task_area, category, …).
    const seedByReq = {};
    prev.forEach(r => {
      if (reqSet.has(r.requirement_id) && !seedByReq[r.requirement_id]) seedByReq[r.requirement_id] = r;
    });
    const kept = prev.filter(r => !reqSet.has(r.requirement_id));
    const expanded = [];
    batchReqIds.forEach(rid => {
      const seed = seedByReq[rid];
      if (!seed) return;
      const cs = cellsByReq[rid] && cellsByReq[rid].length
        ? cellsByReq[rid]
        : [{ submittal_volume: '', eval_factor: 'Administrative / None', eval_sub_factor: '' }];
      cs.forEach((c, i) => {
        expanded.push({
          ...seed,
          id: i === 0 ? seed.id : `${seed.id}__c${i}`,
          submittal_volume: c.submittal_volume || '',
          eval_factor: c.eval_factor || 'Administrative / None',
          eval_sub_factor: c.eval_sub_factor || '',
          requirement_group: c.requirement_group || seed.requirement_group || '',
          // Preserve user-written guidance; only fill from AI when empty.
          writer_guidance: seed.writer_guidance ? seed.writer_guidance : (c.writer_guidance || ''),
        });
      });
    });
    return [...kept, ...expanded];
  };

  // ── AI Build Compliance Matrix (two-phase) ───────────────────────
  const handleAiMap = async () => {
    setGateOpen(false);
    setAiLoading(true);
    try {
      // Phase 1: instant init
      const initRes = await fetch(`${API}/api/pursuits/${pursuitId}/rtm/ai-map/init`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        if (initRes.status === 412) {
          toast.error(describeApiError(err, 'Run AI Shred first'), { duration: 8000 });
        } else {
          toast.error(describeApiError(err, 'Build failed'));
        }
        setAiLoading(false);
        return;
      }
      const initData = await initRes.json();
      const initEntries = initData.entries || [];
      setEntries(initEntries);
      recompute(initEntries);

      const batchStatus = (initData.batch_plan || []).map(() => 'pending');
      setAiDetected({
        batch_plan: initData.batch_plan || [],
        batch_status: batchStatus,
        requirements_total: initData.requirements_total || initEntries.length,
        model_used: '',
      });

      toast.success(
        `Loaded ${initEntries.length} requirements — building matrix in ${initData.batch_plan.length} batches…`
      );

      // Phase 2: parallel batches
      const enrichBatch = async (batchIdx, requirementIds) => {
        try {
          const res = await fetch(`${API}/api/pursuits/${pursuitId}/rtm/ai-map/batch`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ requirement_ids: requirementIds }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { ok: false, batchIdx, requirementIds, error: err };
          }
          const data = await res.json();
          return { ok: true, batchIdx, requirementIds, data };
        } catch {
          return { ok: false, batchIdx, requirementIds, error: { detail: 'Network error' } };
        }
      };

      const apply = (result) => {
        if (result.ok) {
          setEntries(prev => {
            const next = expandWithCells(prev, result.requirementIds, result.data.cells || []);
            recompute(next);
            return next;
          });
          setAiDetected(prev => {
            if (!prev) return prev;
            const nextStatus = [...(prev.batch_status || [])];
            nextStatus[result.batchIdx] = 'ok';
            return { ...prev, batch_status: nextStatus, model_used: result.data.model_used || prev.model_used };
          });
        } else {
          setAiDetected(prev => {
            if (!prev) return prev;
            const nextStatus = [...(prev.batch_status || [])];
            nextStatus[result.batchIdx] = 'failed';
            return { ...prev, batch_status: nextStatus };
          });
        }
      };

      const PARALLEL = 3;
      const queue = (initData.batch_plan || []).map((ids, idx) => ({ idx, ids }));
      const runWorker = async () => {
        while (queue.length > 0) {
          const job = queue.shift();
          if (!job) break;
          apply(await enrichBatch(job.idx, job.ids));
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(PARALLEL, (initData.batch_plan || []).length) }, () => runWorker())
      );
    } catch {
      toast.error('Network error');
    }
    setAiLoading(false);
  };

  // Per-batch retry
  const retryBatch = async (batchIdx) => {
    if (!aiDetected?.batch_plan) return;
    const requirementIds = aiDetected.batch_plan[batchIdx];
    if (!requirementIds?.length) return;
    setAiDetected(prev => {
      if (!prev) return prev;
      const nextStatus = [...(prev.batch_status || [])];
      nextStatus[batchIdx] = 'pending';
      return { ...prev, batch_status: nextStatus };
    });
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/rtm/ai-map/batch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ requirement_ids: requirementIds }),
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(prev => {
          const next = expandWithCells(prev, requirementIds, data.cells || []);
          recompute(next);
          return next;
        });
        setAiDetected(prev => {
          if (!prev) return prev;
          const nextStatus = [...(prev.batch_status || [])];
          nextStatus[batchIdx] = 'ok';
          return { ...prev, batch_status: nextStatus };
        });
        toast.success(`Batch ${batchIdx + 1} enriched`);
      } else {
        setAiDetected(prev => {
          if (!prev) return prev;
          const nextStatus = [...(prev.batch_status || [])];
          nextStatus[batchIdx] = 'failed';
          return { ...prev, batch_status: nextStatus };
        });
        const err = await res.json().catch(() => ({}));
        toast.error(describeApiError(err, 'Batch retry failed'));
      }
    } catch { toast.error('Network error'); }
  };

  // ── Group entries by volume → factor → sub-factor ──────────────
  const grouped = useMemo(() => {
    // Sort key: volume → factor → sub-factor → requirement_group
    const sorted = [...entries].sort((a, b) => {
      const vA = a.submittal_volume || 'ZZZ', vB = b.submittal_volume || 'ZZZ';
      if (vA !== vB) return vA.localeCompare(vB);
      const fA = a.eval_factor || 'ZZZ', fB = b.eval_factor || 'ZZZ';
      if (fA !== fB) return fA.localeCompare(fB);
      const sA = a.eval_sub_factor || '', sB = b.eval_sub_factor || '';
      return sA.localeCompare(sB);
    });

    // Build hierarchy: Map<volume, Map<"factor|||subfactor", Entry[]>>
    const volumes = new Map();
    for (const entry of sorted) {
      const vol = entry.submittal_volume || '— Unassigned';
      const factorKey = `${entry.eval_factor || '— Unassigned'}|||${entry.eval_sub_factor || ''}`;
      if (!volumes.has(vol)) volumes.set(vol, new Map());
      const factors = volumes.get(vol);
      if (!factors.has(factorKey)) factors.set(factorKey, []);
      factors.get(factorKey).push(entry);
    }
    return volumes;
  }, [entries]);

  const handleDismissAi = () => setAiDetected(null);

  return (
    <Card className="border-slate-300 shadow-md" data-testid="rtm-matrix">
      {/* Header */}
      <CardHeader
        className="cursor-pointer border-b border-slate-200 bg-slate-900 rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-amber-400" />
            <CardTitle className="text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Compliance Matrix
            </CardTitle>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
              {entries.length} Reqs
            </Badge>
            {(summary.volumes || 0) > 0 && (
              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs">
                <Layers className="w-3 h-3 mr-1" />{summary.volumes} Vol
              </Badge>
            )}
            {(summary.factors || 0) > 0 && (
              <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 text-xs">
                <Target className="w-3 h-3 mr-1" />{summary.factors} Factor{summary.factors !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {entries.length > 0 && (
              <span className="text-sm text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary.guidance_complete || 0}/{entries.length} guidance
              </span>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          {/* Build Compliance Matrix bar */}
          {!aiDetected && (
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200" data-testid="ai-map-bar">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-800">AI Compliance Matrix Builder</span>
                <span className="text-xs text-amber-600">Maps volumes → eval factors → requirements → writer guidance</span>
              </div>
              <Button
                data-testid="ai-map-btn"
                size="sm" disabled={aiLoading || gateChecking}
                onClick={(e) => { e.stopPropagation(); checkSectionMThenBuild(); }}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1.5"
              >
                {(aiLoading || gateChecking) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {aiLoading ? 'Building...' : gateChecking ? 'Checking…' : 'Build Compliance Matrix'}
              </Button>
            </div>
          )}

          {/* AI Progress Banner */}
          {aiDetected && (
            <div className="px-5 py-3 bg-gradient-to-r from-amber-100 to-orange-100 border-b border-amber-300" data-testid="ai-map-results">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">
                    {aiDetected.batch_status?.includes('pending')
                      ? `Building matrix for ${aiDetected.requirements_total} requirements…`
                      : aiDetected.batch_status?.includes('failed')
                        ? 'Some batches failed — click Retry'
                        : `Compliance Matrix built — ${aiDetected.requirements_total} requirements mapped`}
                  </span>
                  {aiDetected.model_used && (
                    <Badge className="bg-amber-200 text-amber-700 text-[10px]">{aiDetected.model_used}</Badge>
                  )}
                </div>
                <Button data-testid="ai-dismiss-rtm-btn" size="sm" variant="ghost" onClick={handleDismissAi}
                  className="text-slate-500 hover:text-red-500 text-xs gap-1">
                  <X className="w-3 h-3" /> Dismiss
                </Button>
              </div>
              {aiDetected.batch_status?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="rtm-batch-progress">
                  <span className="text-[10px] font-semibold text-amber-700 uppercase mr-1">Batches:</span>
                  {aiDetected.batch_status.map((status, idx) => (
                    <span key={idx}
                      data-testid={`rtm-batch-${idx}-${status}`}
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                        status === 'ok' && 'bg-emerald-100 text-emerald-700',
                        status === 'pending' && 'bg-amber-100 text-amber-700 animate-pulse',
                        status === 'failed' && 'bg-red-100 text-red-700 border border-red-300',
                      )}
                    >
                      Batch {idx + 1}
                      {status === 'failed' && (
                        <button type="button" onClick={() => retryBatch(idx)}
                          className="underline ml-1 hover:text-red-900"
                          data-testid={`rtm-batch-${idx}-retry`}>retry</button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Matrix Context Panel — win themes, scope note, eval factors, etc. */}
          <MatrixContextPanel
            meta={meta}
            onChange={setMeta}
            onSave={saveMeta}
            saving={metaSaving}
          />

          {/* Summary */}
          <div className="px-5 pt-4"><SummaryBar summary={summary} /></div>

          {/* Matrix Body */}
          {entries.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium">No requirements mapped yet</p>
              <p className="text-xs mt-1">Click "Build Compliance Matrix" to auto-map from RFP, or add manually</p>
            </div>
          ) : (
            <div data-testid="rtm-entries-list">
              <CollapsibleList count={entries.length} label="requirements" testId="rtm-entries-collapsible">
                <ColumnHeaders />
                {Array.from(grouped.entries()).map(([volume, factors]) => (
                  <div key={volume}>
                    <VolumeHeader label={volume} />
                    {Array.from(factors.entries()).map(([factorKey, rows]) => {
                      const [factor, subFactor] = factorKey.split('|||');
                      return (
                        <div key={factorKey}>
                          <FactorHeader
                            label={factor}
                            subFactor={subFactor || null}
                            count={rows.length}
                          />
                          {rows.map(entry => (
                            <RTMRow
                              key={entry.id}
                              entry={entry}
                              onUpdate={handleUpdateEntry}
                              onDelete={handleDeleteEntry}
                              onOpenGuidance={openGuidance}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </CollapsibleList>
            </div>
          )}

          {/* Actions Footer */}
          <div className="px-5 py-4 bg-white border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Button data-testid="rtm-save-btn" onClick={handleSave} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}
                </Button>
                <Button data-testid="rtm-pdf-btn" variant="outline" size="sm"
                  onClick={handleDownloadPdf} disabled={downloading || entries.length === 0}>
                  <Download className="w-4 h-4 mr-1" />{downloading ? 'Generating...' : 'PDF'}
                </Button>
                <Button data-testid="rtm-docx-btn" variant="outline" size="sm"
                  onClick={handleDownloadDocx} disabled={downloading || entries.length === 0}>
                  <Download className="w-4 h-4 mr-1" />{downloading ? 'Generating...' : 'Word'}
                </Button>
                <Button data-testid="rtm-add-btn" variant="outline" size="sm"
                  onClick={() => setAddModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />Add Requirement
                </Button>
              </div>
              {lastSaved && (
                <p className="text-xs text-slate-400">Last saved: {new Date(lastSaved).toLocaleString()}</p>
              )}
            </div>
          </div>

          {/* Gap Check — submission gate, lives below the matrix */}
          <GapCheckPanel
            entries={entries}
            pursuitId={pursuitId}
            token={token}
            onEntriesUpdate={setEntries}
          />
        </CardContent>
      )}

      {/* ── Add Requirement Modal ─────────────────────────────────── */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="max-w-xl" data-testid="add-rtm-modal">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-amber-500" />
              Add Requirement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Requirement ID</label>
                <Input data-testid="add-rtm-id"
                  value={newEntry.requirement_id}
                  onChange={e => setNewEntry(p => ({ ...p, requirement_id: e.target.value }))}
                  placeholder="REQ-001" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Section Reference</label>
                <Input
                  value={newEntry.section_reference}
                  onChange={e => setNewEntry(p => ({ ...p, section_reference: e.target.value }))}
                  placeholder="L.5.1" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Requirement Text *</label>
              <Textarea data-testid="add-rtm-text"
                value={newEntry.requirement_text}
                onChange={e => setNewEntry(p => ({ ...p, requirement_text: e.target.value }))}
                placeholder="The contractor shall..."
                className="min-h-[60px]" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Submittal Volume</label>
              <Input
                list="rtm-volume-suggestions"
                value={newEntry.submittal_volume}
                onChange={e => setNewEntry(p => ({ ...p, submittal_volume: e.target.value }))}
                placeholder="VOLUME I — TECHNICAL" />
              <datalist id="rtm-volume-suggestions">
                {volumeSuggestions.map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Evaluation Factor</label>
                <Input
                  list="rtm-factor-suggestions"
                  value={newEntry.eval_factor}
                  onChange={e => setNewEntry(p => ({ ...p, eval_factor: e.target.value }))}
                  placeholder="Factor 1: Technical Capability" />
                <datalist id="rtm-factor-suggestions">
                  {factorSuggestions.map(f => <option key={f} value={f} />)}
                </datalist>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Sub-Factor / Element</label>
                <Input
                  value={newEntry.eval_sub_factor}
                  onChange={e => setNewEntry(p => ({ ...p, eval_sub_factor: e.target.value }))}
                  placeholder="Element 1.1: Transition Plan" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Requirement Group</label>
                <Input
                  value={newEntry.requirement_group}
                  onChange={e => setNewEntry(p => ({ ...p, requirement_group: e.target.value }))}
                  placeholder="e.g. Personnel Requirements" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Win Theme #
                  <span className="text-[10px] text-slate-400 ml-1">(e.g. 1, 3 or N/A)</span>
                </label>
                <Input
                  value={newEntry.win_theme}
                  onChange={e => setNewEntry(p => ({ ...p, win_theme: e.target.value }))}
                  placeholder="1, 3" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">
                Writer Guidance
                <span className="text-[10px] text-slate-400 ml-2">(actionable notes for tech writers)</span>
              </label>
              <Textarea
                value={newEntry.writer_guidance}
                onChange={e => setNewEntry(p => ({ ...p, writer_guidance: e.target.value }))}
                placeholder="Lead with... cite... highlight... CRITICAL: ..."
                className="min-h-[70px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>Cancel</Button>
            <Button data-testid="add-rtm-submit" onClick={handleAddEntry}>Add Requirement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Writer Guidance Dialog ────────────────────────────────── */}
      <Dialog open={guidanceOpen} onOpenChange={setGuidanceOpen}>
        <DialogContent className="sm:max-w-[600px]" data-testid="writer-guidance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-amber-500" />
              Writer Guidance
            </DialogTitle>
            {guidanceEntry && (
              <p className="text-xs text-slate-500 leading-snug pt-1">
                <span className="font-mono font-bold text-slate-600 mr-2">
                  {guidanceEntry.section_reference || '—'}
                </span>
                {guidanceEntry.requirement_text?.substring(0, 140)}
                {(guidanceEntry.requirement_text?.length || 0) > 140 ? '…' : ''}
              </p>
            )}
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-slate-500 mb-2">
              Actionable guidance for the technical writer responding to this requirement.
              AI-generated on first run — edit freely. Your changes are preserved across AI re-runs.
            </p>
            <Textarea
              value={guidanceDraft}
              onChange={(e) => setGuidanceDraft(e.target.value)}
              placeholder="e.g. Lead with FedRAMP-Moderate authorization date; cite Page 14 of the prior CPARS; keep under 1 page. CRITICAL: Do not exceed page limit..."
              className="min-h-[160px] text-sm"
              data-testid="writer-guidance-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuidanceOpen(false)}>Cancel</Button>
            <Button onClick={saveGuidance} data-testid="writer-guidance-save-btn">
              Save Guidance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Section M Gate Dialog ─────────────────────────────────── */}
      <Dialog open={gateOpen} onOpenChange={(v) => { if (!extractingSectionM) setGateOpen(v); }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="section-m-gate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-500" />
              Extract Section M first?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm text-slate-600">
            <p>
              No <strong>Evaluation Factors (Section M)</strong> have been extracted for this
              pursuit yet. The Compliance Matrix maps each requirement to its
              <strong> submittal volume → evaluation factor</strong>, so building without
              Section M produces a weaker matrix with no factor hierarchy.
            </p>
            <p className="text-xs text-slate-500">
              Recommended: extract Submittal Volumes &amp; Evaluation Factors now (runs the
              same extractor as the Strategy stage), then build the matrix.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => { setGateOpen(false); handleAiMap(); }}
              disabled={extractingSectionM}
              data-testid="gate-build-anyway-btn"
            >
              Build anyway
            </Button>
            <Button
              onClick={extractSectionMThenBuild}
              disabled={extractingSectionM}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="gate-extract-btn"
            >
              {extractingSectionM
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Extracting…</>
                : <><Sparkles className="w-4 h-4 mr-1" /> Extract Section M &amp; build</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
